import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/shared/utils/cn';

type CustomCalendarProps = {
  mode?: 'single' | 'range';
  selected?: Date;
  onSelect?: (date: Date) => void;
  startDate?: Date;
  endDate?: Date;
  onStartSelect?: (date: Date) => void;
  onEndSelect?: (date: Date) => void;
  disabled?: (date: Date) => boolean;
  className?: string;
};

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONTHS = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

export const CustomCalendar = ({
  mode = 'single',
  selected,
  onSelect,
  startDate,
  endDate,
  onStartSelect,
  onEndSelect,
  disabled,
  className,
}: CustomCalendarProps) => {
  const [currentMonth, setCurrentMonth] = useState(
    selected || startDate ? new Date(selected || startDate!) : new Date(),
  );

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();

  // Get the day of week for the first day (0 = Sunday, 1 = Monday, etc.)
  // Adjust so Monday = 0
  const firstDayWeekday = (firstDayOfMonth.getDay() + 6) % 7;

  // Get previous month's last days to fill the grid
  const prevMonthLastDay = new Date(year, month, 0).getDate();
  const prevMonthDays = Array.from(
    { length: firstDayWeekday },
    (_, i) => prevMonthLastDay - firstDayWeekday + i + 1,
  );

  // Get current month days
  const currentMonthDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  // Get next month days to fill the grid (up to 42 cells - 6 rows)
  const totalCells = 42;
  const remainingCells =
    totalCells - prevMonthDays.length - currentMonthDays.length;
  const nextMonthDays = Array.from({ length: remainingCells }, (_, i) => i + 1);

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(year, month - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(year, month + 1, 1));
  };

  const handleDateSelect = (
    day: number,
    isPrevMonth: boolean,
    isNextMonth: boolean,
  ) => {
    const selectedDate = isPrevMonth
      ? new Date(year, month - 1, day)
      : isNextMonth
        ? new Date(year, month + 1, day)
        : new Date(year, month, day);

    if (disabled && disabled(selectedDate)) return;

    if (mode === 'single') {
      if (onSelect) {
        onSelect(selectedDate);
      }
    } else if (mode === 'range') {
      // Range mode: first click sets start, second sets end
      if (
        !startDate ||
        !(startDate instanceof Date) ||
        (startDate instanceof Date && endDate instanceof Date)
      ) {
        // Start new range
        if (onStartSelect) onStartSelect(selectedDate);
        if (onEndSelect) onEndSelect(undefined!);
      } else if (startDate instanceof Date && !endDate) {
        // Set end date
        if (selectedDate < startDate) {
          // If selected date is before start, swap them
          if (onEndSelect) onEndSelect(startDate);
          if (onStartSelect) onStartSelect(selectedDate);
        } else {
          if (onEndSelect) onEndSelect(selectedDate);
        }
      }
    }
  };

  const isSelected = (
    day: number,
    isPrevMonth: boolean,
    isNextMonth: boolean,
  ) => {
    const checkDate = isPrevMonth
      ? new Date(year, month - 1, day)
      : isNextMonth
        ? new Date(year, month + 1, day)
        : new Date(year, month, day);

    if (mode === 'single' && selected) {
      return (
        selected.getDate() === checkDate.getDate() &&
        selected.getMonth() === checkDate.getMonth() &&
        selected.getFullYear() === checkDate.getFullYear()
      );
    }

    if (mode === 'range') {
      const isSameAsStart =
        startDate &&
        startDate instanceof Date &&
        startDate.getDate() === checkDate.getDate() &&
        startDate.getMonth() === checkDate.getMonth() &&
        startDate.getFullYear() === checkDate.getFullYear();

      const isSameAsEnd =
        endDate &&
        endDate instanceof Date &&
        endDate.getDate() === checkDate.getDate() &&
        endDate.getMonth() === checkDate.getMonth() &&
        endDate.getFullYear() === checkDate.getFullYear();

      return isSameAsStart || isSameAsEnd;
    }

    return false;
  };

  const isInRange = (
    day: number,
    isPrevMonth: boolean,
    isNextMonth: boolean,
  ) => {
    if (
      mode !== 'range' ||
      !startDate ||
      !endDate ||
      !(startDate instanceof Date) ||
      !(endDate instanceof Date)
    )
      return false;

    const checkDate = isPrevMonth
      ? new Date(year, month - 1, day)
      : isNextMonth
        ? new Date(year, month + 1, day)
        : new Date(year, month, day);

    return checkDate > startDate && checkDate < endDate;
  };

  const isInRangeOrSelected = (
    day: number,
    isPrevMonth: boolean,
    isNextMonth: boolean,
  ) => {
    return (
      isSelected(day, isPrevMonth, isNextMonth) ||
      isInRange(day, isPrevMonth, isNextMonth)
    );
  };

  const isDisabled = (
    day: number,
    isPrevMonth: boolean,
    isNextMonth: boolean,
  ) => {
    if (!disabled) return false;

    const checkDate = isPrevMonth
      ? new Date(year, month - 1, day)
      : isNextMonth
        ? new Date(year, month + 1, day)
        : new Date(year, month, day);

    return disabled(checkDate);
  };

  // Create all days with metadata
  const allDays = [
    ...prevMonthDays.map((day) => ({
      day,
      isPrevMonth: true,
      isNextMonth: false,
    })),
    ...currentMonthDays.map((day) => ({
      day,
      isPrevMonth: false,
      isNextMonth: false,
    })),
    ...nextMonthDays.map((day) => ({
      day,
      isPrevMonth: false,
      isNextMonth: true,
    })),
  ];

  // Split into rows of 7
  const rows: (typeof allDays)[] = [];
  for (let i = 0; i < allDays.length; i += 7) {
    rows.push(allDays.slice(i, i + 7));
  }

  // Helper to render a day button
  const renderDayButton = (
    day: number,
    isPrevMonth: boolean,
    isNextMonth: boolean,
    key: string,
  ) => {
    const isCurrentMonth = !isPrevMonth && !isNextMonth;
    return (
      <button
        key={key}
        type="button"
        onClick={() => handleDateSelect(day, isPrevMonth, isNextMonth)}
        disabled={isDisabled(day, isPrevMonth, isNextMonth)}
        className={cn(
          'relative z-10 flex h-8 w-8 items-center justify-center rounded-full text-[13px] font-normal transition-all',
          isCurrentMonth ? 'text-white' : 'text-white/40',
          'hover:bg-white/10',
          isSelected(day, isPrevMonth, isNextMonth) &&
            'bg-white/20 text-white hover:bg-white/20',
          isDisabled(day, isPrevMonth, isNextMonth) &&
            'cursor-not-allowed opacity-30 hover:bg-transparent',
        )}
      >
        {day}
      </button>
    );
  };

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {/* Header with month/year navigation */}
      <div className="flex items-center justify-between px-2">
        <button
          type="button"
          onClick={goToPreviousMonth}
          className="flex items-center justify-center rounded p-1 text-white transition-opacity hover:opacity-80"
        >
          <ChevronLeft className="size-5" />
        </button>

        <div className="flex items-center gap-1">
          <span className="text-[13px] font-normal leading-[18px] tracking-[-0.4px] text-white">
            {MONTHS[month]} {year}
          </span>
          <ChevronDown className="size-4 text-white" />
        </div>

        <button
          type="button"
          onClick={goToNextMonth}
          className="flex items-center justify-center rounded p-1 text-white transition-opacity hover:opacity-80"
        >
          <ChevronRight className="size-5" />
        </button>
      </div>

      {/* Calendar grid */}
      <div className="flex flex-col overflow-hidden">
        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-1 pb-2">
          {WEEKDAYS.map((day) => (
            <div
              key={day}
              className="flex h-8 items-center justify-center text-[11px] font-normal text-white/60"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Date grid */}
        <div className="flex flex-col gap-1">
          {rows.map((row, rowIndex) => {
            // Calculate if this row has a range selection
            const rangeIndices: number[] = [];
            row.forEach((dayData, colIndex) => {
              if (
                isInRangeOrSelected(
                  dayData.day,
                  dayData.isPrevMonth,
                  dayData.isNextMonth,
                )
              ) {
                rangeIndices.push(colIndex);
              }
            });

            const hasRange = rangeIndices.length > 0;
            const rangeStart = hasRange ? Math.min(...rangeIndices) : 0;
            const rangeEnd = hasRange ? Math.max(...rangeIndices) : 0;

            return (
              <div
                key={rowIndex}
                className="relative grid grid-cols-7 place-items-center gap-1"
              >
                {/* Range background pill */}
                {hasRange && mode === 'range' && (
                  <div
                    className="absolute top-1/2 z-0 h-8 -translate-y-1/2 rounded-full bg-white/30"
                    style={{
                      left: `calc(${rangeStart} * ((100% - 1.5rem) / 7 + 0.25rem) + ((100% - 1.5rem) / 7 - 2rem) / 2)`,
                      width: `calc((${rangeEnd} - ${rangeStart}) * ((100% - 1.5rem) / 7 + 0.25rem) + 2rem)`,
                    }}
                  />
                )}
                {/* Day buttons */}
                {row.map((dayData, colIndex) =>
                  renderDayButton(
                    dayData.day,
                    dayData.isPrevMonth,
                    dayData.isNextMonth,
                    `${rowIndex}-${colIndex}`,
                  ),
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
